import { EventType } from "./types";
import {
  differenceInMinutes,
  isBefore,
  set,
  setMilliseconds,
  setSeconds,
  startOfDay,
} from "date-fns";

const combineDateAndTime = (date: Date, time: Date): Date =>
  set(date, { hours: time.getHours(), minutes: time.getMinutes() });

export const dateRangeIsValid = (
  startDate?: Date,
  endDate?: Date,
  allDay?: boolean,
): {
  isValid: boolean;
  error?: string;
} => {
  if (!startDate) {
    return {
      error: "Please enter a start time",
      isValid: false,
    };
  }
  if (!endDate) {
    return {
      error: "Please enter a end time",
      isValid: false,
    };
  }

  const minStartDate = allDay
    ? startOfDay(new Date())
    : setSeconds(setMilliseconds(Date.now(), 0), 0);

  if (isBefore(startDate, minStartDate)) {
    return {
      error: "Unable to book lessons in the past",
      isValid: false,
    };
  }

  if (isBefore(endDate, startDate)) {
    return {
      error: "Start time must be before the end time",
      isValid: false,
    };
  }

  return {
    isValid: true,
  };
};

export const timeRangeIsValid = (
  startTime: Date,
  endTime: Date,
  minutes = 60,
): {
  isValid: boolean;
  error?: string;
} =>
  differenceInMinutes(endTime, startTime) >= minutes
    ? { isValid: true }
    : {
        isValid: false,
        error: `Duration needs to be ${minutes} minutes or more`,
      };

export const dateTimeRangeIsValid = (
  startDate: Date | null | undefined,
  startTime: Date | null | undefined,
  endDate: Date | null | undefined,
  endTime: Date | null | undefined,
  selectedEventType?: EventType,
): {
  isValid: boolean;
  error?: string;
} => {
  if (!startDate || !startTime) {
    return {
      error: "Please enter a start date and time",
      isValid: false,
    };
  }
  if (!endDate || !endTime) {
    return {
      error: "Please enter a end date and time",
      isValid: false,
    };
  }

  const isRangeValid = dateRangeIsValid(
    combineDateAndTime(startDate, startTime),
    combineDateAndTime(endDate, endTime),
  );

  if (!isRangeValid.isValid) {
    return isRangeValid;
  }

  const eventTypeIsLesson = Boolean(
    selectedEventType &&
      [
        EventType.PaidLesson,
        EventType.NewLesson,
        EventType.UnpaidLesson,
      ].includes(selectedEventType),
  );

  const eventTypeIsUnavailable = selectedEventType === EventType.Unavailable;

  const isTimeRangeValid = timeRangeIsValid(
    setSeconds(setMilliseconds(startTime, 0), 0),
    setSeconds(setMilliseconds(endTime, 0), 0),
    eventTypeIsUnavailable ? 15 : undefined,
  );

  if (
    (eventTypeIsLesson || eventTypeIsUnavailable) &&
    !isTimeRangeValid.isValid
  ) {
    return isTimeRangeValid;
  }

  return {
    isValid: true,
  };
};

export const isLessonMinutesIncrementValid = (check: Date): boolean => {
  const minutes: number = check.getMinutes();

  return minutes % 15 === 0;
};
